/**
 * truchetPipeline — morphing Truchet tile patterns.
 *
 * Truchet tiles are square tiles with quarter-circle arcs in their
 * corners. When tiled, they create intricate looping patterns. We
 * render a grid of such tiles, where each tile randomly picks between
 * two orientations, and animate the arcs by varying their thickness
 * with audio.
 *
 * The result is a high-density, mesmerizing pattern that feels alive.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TruchetPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float sdArc(vec2 p, vec2 center, float radius, float startAngle, float endAngle) {
  vec2 d = p - center;
  float r = length(d);
  float a = atan(d.y, d.x);
  // Normalize angle to [startAngle, startAngle + 2*PI].
  float twoPi = 6.2831853;
  a = mod(a - startAngle, twoPi) + startAngle;
  // Distance to the arc.
  float dToArc = abs(r - radius);
  // Angle constraint.
  float angleMid = (startAngle + endAngle) * 0.5;
  float angleHalfWidth = (endAngle - startAngle) * 0.5;
  float dToAngle = abs(mod(a - angleMid + 3.14159, twoPi) - 3.14159) - angleHalfWidth;
  return max(dToArc, dToAngle);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Scale: number of tiles per screen.
  float tileSize = 0.05 + mid * 0.02;
  vec2 tileP = p / tileSize;
  // Get the tile coords and local position within the tile.
  vec2 tileId = floor(tileP);
  vec2 local = fract(tileP) - 0.5;  // -0.5..0.5

  // Random orientation per tile.
  float h = hash21(tileId);
  bool orientation = h > 0.5;
  // Also slow phase animation.
  float phase = uTime * 0.3 + h * 6.2831853;
  // Audio: vary tile size and pattern density.
  float arcRadius = 0.45;

  // Two arcs per tile: in opposite corners.
  // Type A: arcs at (0, 0) and (1, 1).
  // Type B: arcs at (0, 1) and (1, 0).
  float d = 100.0;
  vec3 color = vec3(0.0);
  if (orientation) {
    d = min(d, sdArc(local, vec2(-0.5, -0.5), arcRadius, 0.0, 1.5707963));
    d = min(d, sdArc(local, vec2( 0.5,  0.5), arcRadius, 3.14159265, 4.71238898));
  } else {
    d = min(d, sdArc(local, vec2(-0.5,  0.5), arcRadius, -1.5707963, 0.0));
    d = min(d, sdArc(local, vec2( 0.5, -0.5), arcRadius, 1.5707963, 3.14159265));
  }

  // Animate arc thickness with audio.
  float thickness = 0.06 + 0.02 * sin(phase);
  // Color: tile bg + arc.
  vec3 bg = vec3(0.04, 0.06, 0.10);
  // Per-tile color shift.
  float hue = h + uTime * 0.05;
  vec3 arcColor = vec3(
    0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
    0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
    0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
  );
  // Boost with audio.
  arcColor *= 1.0 + high * 0.5;
  // Mix between bg and arc.
  vec3 col = mix(bg, arcColor, smoothstep(thickness, 0.0, d));

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeTruchetBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeTruchetRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTruchetPipeline(gl: WebGL2RenderingContext): TruchetPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTruchet(
  gl: WebGL2RenderingContext,
  pipe: TruchetPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
